import React, { useEffect, useState } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useApp, ConnectPrompt } from "@/App";
import { isSafeOwner, getOperatorNonce, operatorLogin, getLaunchStatus } from "@/api";
import { WhitepaperEditor } from "@/components/operator/WhitepaperEditor";
import { HubFlags } from "@/components/operator/HubFlags";
import { PoolSetup, TokenSettings } from "@/components/operator/PoolSetup";

const KEY = "flame.operator_token";

export default function OperatorPage() {
  const { address, isConnected } = useAccount();
  const { showToast } = useApp();
  const { signMessageAsync } = useSignMessage();
  const [owner, setOwner] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem(KEY));

  useEffect(() => { if (address) isSafeOwner(address).then(setOwner).catch(() => setOwner(false)); }, [address]);
  useEffect(() => {
    if (token) getLaunchStatus(token).catch((e) => { if (e?.response?.status === 401) { sessionStorage.removeItem(KEY); setToken(null); } });
  }, [token]);
  if (!isConnected) return <ConnectPrompt />;
  if (owner === null) return <div className="pt-32 text-center text-slate-400">Checking Safe ownership…</div>;
  if (!owner) return <div className="pt-32 text-center text-slate-400" data-testid="operator-not-owner">This console is only for treasury Safe owners.</div>;

  const login = async () => {
    try {
      const { message } = await getOperatorNonce(address);
      const signature = await signMessageAsync({ message });
      const r = await operatorLogin(address, signature);
      sessionStorage.setItem(KEY, r.token); setToken(r.token);
    } catch (e) { showToast(e?.response?.data?.detail || e.shortMessage || "Login failed", "error"); }
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-display text-4xl font-black flex items-center gap-2"><ShieldCheck className="text-emerald-400" /> Operator Console</h1>
        {!token ? (
          <div className="mt-6 card-tile card-flame max-w-lg" data-testid="operator-login">
            <p className="text-sm text-slate-300">Sign a free message with your Safe-owner wallet to unlock the console. No transaction, no gas.</p>
            <button data-testid="operator-login-button" onClick={login} className="btn-tactile btn-flame mt-3">Sign in with wallet</button>
          </div>
        ) : (
          <Tabs defaultValue="whitepaper" className="mt-6">
            <TabsList className="bg-black/40">
              <TabsTrigger value="whitepaper" data-testid="operator-tab-whitepaper">White Paper</TabsTrigger>
              <TabsTrigger value="pool" data-testid="operator-tab-pool">Pool Setup</TabsTrigger>
              <TabsTrigger value="settings" data-testid="operator-tab-settings">Token Settings</TabsTrigger>
              <TabsTrigger value="hubs" data-testid="operator-tab-hubs">Hub Flags</TabsTrigger>
            </TabsList>
            <TabsContent value="whitepaper" className="mt-4"><WhitepaperEditor token={token} showToast={showToast} /></TabsContent>
            <TabsContent value="pool" className="mt-4"><PoolSetup token={token} showToast={showToast} /></TabsContent>
            <TabsContent value="hubs" className="mt-4"><HubFlags token={token} showToast={showToast} /></TabsContent>
            <TabsContent value="settings" className="mt-4"><TokenSettings token={token} showToast={showToast} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
